import { useEffect, useState } from "react";
import type { Person } from "../../shared/types.ts";
import { Face } from "./Face.tsx";

const STORAGE_KEY = "chores.actor";

/**
 * Who gets the credit for the next tick.
 *
 * The kitchen tablet is one shared, permanently signed-in session, so "who is
 * doing this" is a choice at tick time rather than an identity. Whoever walks
 * up taps their own face first; everything they tick after that is theirs, and
 * it sticks across reloads because a wall tablet is never freshly opened.
 *
 * A kid session cannot use this — the server credits kids to themselves
 * whatever the request body says, so offering the choice would be a lie.
 */
export function useActor(people: Person[], fallback: number | null) {
  const [actorId, setActorId] = useState<number | null>(() => {
    const stored = Number(window.localStorage.getItem(STORAGE_KEY));
    return Number.isInteger(stored) && stored > 0 ? stored : fallback;
  });

  // A person can be deleted while a tablet is showing them.
  useEffect(() => {
    if (people.length === 0) return;
    if (!people.some((p) => p.id === actorId)) setActorId(fallback ?? people[0]!.id);
  }, [people, actorId, fallback]);

  useEffect(() => {
    if (actorId === null) window.localStorage.removeItem(STORAGE_KEY);
    else window.localStorage.setItem(STORAGE_KEY, String(actorId));
  }, [actorId]);

  return { actorId, setActorId };
}

export function ActorPicker({
  people,
  actorId,
  onChange,
}: {
  people: Person[];
  actorId: number | null;
  onChange: (id: number) => void;
}) {
  if (people.length === 0) return null;

  return (
    <div className="crew" role="group" aria-label="Who is doing these jobs">
      <span className="eyebrow crew__label">Crediting</span>
      {people.map((person) => (
        <button
          key={person.id}
          type="button"
          className="crew__person"
          aria-pressed={person.id === actorId}
          onClick={() => onChange(person.id)}
        >
          <Face person={person} size="sm" />
          {person.name}
        </button>
      ))}
    </div>
  );
}
